import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  Stack,
  Button,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { settingsCache, apiCache } from '@/services';
import type { IndustrySkill, UserSkills } from '@/types';
import { parseIndustrySkill, sortSkillsByProgression } from '@/utils';

export const SkillsManager: React.FC = () => {
  const [allSkills, setAllSkills] = useState<IndustrySkill[]>([]);
  const [userSkills, setUserSkills] = useState<UserSkills>({});
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [skills, savedSkills] = await Promise.all([
          apiCache.getIndustrySkills(),
          settingsCache.getUserSkills(),
        ]);
        setAllSkills(skills);
        setUserSkills(savedSkills);
      } catch (err) {
        console.error('Failed to load skills:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSkillChange = (skillName: string, level: number) => {
    setUserSkills((prev) => ({
      ...prev,
      [skillName]: level,
    }));
    setSaved(false);
  };

  const handleRemoveSkill = (skillName: string) => {
    setUserSkills((prev) => {
      const updated = { ...prev };
      delete updated[skillName];
      return updated;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      await settingsCache.setSkillLevels(userSkills);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save skills:', err);
    }
  };

  const handleClearAll = async () => {
    try {
      await settingsCache.clearUserSkills();
      setUserSkills({});
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to clear skills:', err);
    }
  };

  const getEfficiencyDisplay = (skillName: string, level: number): string => {
    const skill = allSkills.find((s) => s.name === skillName);
    if (!skill || level < 1) return '';

    const parsed = parseIndustrySkill(skill);
    const efficiency = parsed.efficiencyByLevel[level - 1];
    if (efficiency === undefined) return '';

    return `${efficiency}% material reduction`;
  };

  // Sort skills by progression: Base -> Advanced -> Expert, grouped by base skill
  const sortedSkills = React.useMemo(() => {
    const skillNames = allSkills.map((s) => s.name);
    const sortedNames = sortSkillsByProgression(skillNames);
    return sortedNames
      .map((name) => allSkills.find((s) => s.name === name))
      .filter((s): s is IndustrySkill => s !== undefined);
  }, [allSkills]);

  const trainedSkillsCount = Object.keys(userSkills).length;

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading skills...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Your Skills</Typography>
              <Typography variant="caption" color="text.secondary">
                {trainedSkillsCount} skill{trainedSkillsCount !== 1 ? 's' : ''} configured
              </Typography>
            </Box>
            <IconButton onClick={() => setExpanded(!expanded)}>
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: '0.3s',
                }}
              />
            </IconButton>
          </Box>

          <Collapse in={expanded}>
            <Stack spacing={2}>
              {saved && (
                <Alert severity="success" onClose={() => setSaved(false)}>
                  Skills saved successfully!
                </Alert>
              )}

              {sortedSkills.map((skill) => {
                const level = userSkills[skill.name] || 0;
                const isTrained = level > 0;

                return (
                  <Box
                    key={skill.name}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: isTrained ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      bgcolor: isTrained ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1">{skill.name}</Typography>
                      {isTrained && (
                        <IconButton size="small" onClick={() => handleRemoveSkill(skill.name)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    <Slider
                      value={level}
                      onChange={(_, value) => handleSkillChange(skill.name, value as number)}
                      min={0}
                      max={5}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                      sx={{ mt: 1 }}
                    />

                    {isTrained && (
                      <Typography variant="caption" color="primary">
                        Level {level} - {getEfficiencyDisplay(skill.name, level)}
                      </Typography>
                    )}
                  </Box>
                );
              })}

              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={handleSave} fullWidth>
                  Save Skills
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  disabled={trainedSkillsCount === 0}
                >
                  Clear All
                </Button>
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};
